import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../ui/card';
import { Star, TrendingUp, Award, GraduationCap, Briefcase, UserRound, ChevronRight } from 'lucide-react';

const CATEGORIES = [
  { key: 'profile', label: 'Profile completeness', icon: UserRound, tip: 'Complete both profile tabs', link: null },
  { key: 'certifications', label: 'Certifications', icon: GraduationCap, tip: 'Pass a TA certification', link: '/certifications' },
  { key: 'awards', label: 'TA Awards', icon: Award, tip: 'Nominate yourself for the Global TA Awards', link: '/awards' },
  { key: 'activity', label: 'Platform activity', icon: TrendingUp, tip: 'Run Market Intelligence & SmartMatch searches', link: '/sourcing-studio' },
  { key: 'jobs', label: 'Jobs posted', icon: Briefcase, tip: 'Post a job to the board', link: '/post-job' }
];

const LEVEL_STYLES = {
  'Getting Started': 'bg-slate-100 text-slate-700 border-slate-300',
  'Rising': 'bg-blue-100 text-blue-700 border-blue-300',
  'Established': 'bg-purple-100 text-purple-700 border-purple-300',
  'Elite': 'bg-amber-100 text-amber-700 border-amber-300'
};

export const RecruiterScoreCard = ({ scoreData }) => {
  const navigate = useNavigate();
  if (!scoreData) return null;

  const { score = 0, level = 'Getting Started', overall_completion = 0, breakdown = {}, max = {} } = scoreData;
  const boosts = CATEGORIES.filter(c => (breakdown[c.key] || 0) < (max[c.key] || 0));

  return (
    <Card data-testid="recruiter-score-card" className="mb-6 border-2 border-blue-200 bg-gradient-to-r from-blue-50 to-purple-50">
      <CardContent className="pt-6">
        <div className="flex flex-col md:flex-row gap-6">
          {/* Score dial */}
          <div className="flex flex-col items-center justify-center md:w-48 shrink-0">
            <div className="relative w-32 h-32">
              <svg viewBox="0 0 120 120" className="w-full h-full -rotate-90">
                <circle cx="60" cy="60" r="52" fill="none" stroke="#e2e8f0" strokeWidth="10" />
                <circle
                  cx="60" cy="60" r="52" fill="none"
                  stroke="url(#scoreGrad)" strokeWidth="10" strokeLinecap="round"
                  strokeDasharray={`${(score / 100) * 326.7} 326.7`}
                  className="transition-all duration-700"
                />
                <defs>
                  <linearGradient id="scoreGrad" x1="0" y1="0" x2="1" y2="1">
                    <stop offset="0%" stopColor="#3b82f6" />
                    <stop offset="100%" stopColor="#a855f7" />
                  </linearGradient>
                </defs>
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <span data-testid="recruiter-score-value" className="text-4xl font-bold text-slate-900">{score}</span>
                <span className="text-xs text-slate-500">/ 100</span>
              </div>
            </div>
            <span data-testid="recruiter-score-level" className={`mt-3 px-3 py-1 rounded-full text-xs font-bold border ${LEVEL_STYLES[level] || LEVEL_STYLES['Getting Started']}`}>
              <Star className="w-3 h-3 inline mr-1 -mt-0.5" />{level}
            </span>
          </div>

          {/* Breakdown */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between mb-1">
              <h3 className="font-bold text-lg text-slate-900">Recruiter Score</h3>
              <span className="text-sm text-slate-600">Overall profile: <strong>{overall_completion}%</strong></span>
            </div>
            <p className="text-sm text-slate-600 mb-4">One score across your profile, certifications, awards, activity and jobs — visible to employers.</p>
            <div className="space-y-2.5">
              {CATEGORIES.map(({ key, label, icon: Icon }) => {
                const pts = breakdown[key] || 0;
                const cap = max[key] || 1;
                return (
                  <div key={key} className="flex items-center gap-3">
                    <Icon className="w-4 h-4 text-slate-500 shrink-0" />
                    <span className="text-sm text-slate-700 w-40 shrink-0 truncate">{label}</span>
                    <div className="flex-1 bg-slate-200 rounded-full h-2">
                      <div className="h-2 rounded-full bg-gradient-to-r from-blue-500 to-purple-500 transition-all" style={{ width: `${(pts / cap) * 100}%` }} />
                    </div>
                    <span className="text-xs font-semibold text-slate-600 w-14 text-right shrink-0">{pts}/{cap} pts</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Boost tips */}
        {boosts.length > 0 && (
          <div className="mt-5 pt-4 border-t border-blue-200/60">
            <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">Boost your score</p>
            <div className="flex flex-wrap gap-2">
              {boosts.map(({ key, tip, link, icon: Icon }) => {
                const gain = (scoreData.max[key] || 0) - (breakdown[key] || 0);
                return (
                  <button
                    key={key}
                    type="button"
                    data-testid={`boost-${key}`}
                    onClick={() => link && navigate(link)}
                    className={`flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full border border-blue-300 bg-white text-blue-700 transition-all ${link ? 'hover:bg-blue-50 hover:shadow-sm cursor-pointer' : 'cursor-default'}`}
                  >
                    <Icon className="w-3 h-3" />
                    {tip} <span className="text-purple-600">+{gain}</span>
                    {link && <ChevronRight className="w-3 h-3" />}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default RecruiterScoreCard;
